import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  CircularProgress,
  TextField,
  InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ApplicationState, AppActionCreators } from '../store';
import { ApplicationContext } from '../context/Contexts';
import { refreshTokenShared } from '../services/RefreshCoordinator';
import { RoleClaimsEnum } from '../helpers/Constants';
import { captureAuthError } from '../helpers/Sentry';
import { hasRight } from '../utils/general';
import { Organization } from '../interfaces/Organization';
import { UpdateOrgToUserRequestDto } from '../interfaces/AppUser';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 30px 5px 16px',
      minHeight: 'unset',
      '&:focus': {
        backgroundColor: 'transparent',
        borderRadius: 20
      }
    },
    '& .MuiSelect-icon': {
      color: '#fff',
      right: 8
    }
  },
  label: {
    maxWidth: 220,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  searchBox: {
    padding: theme.spacing(1.5)
  },
  menuItem: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

const OrganizationsBadge = () => {
  const classes = useStyles();
  const context = useContext(ApplicationContext);
  const translatorService = context.translatorService;
  const appUserService = context.appUserService;

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const user = useSelector((state: ApplicationState) => state.app?.user);
  const appUser = useSelector((state: ApplicationState) => state.app?.appUser);
  const refreshToken = useSelector((state: ApplicationState) => state.app?.refreshToken);
  const userOrganizations = useSelector((state: ApplicationState) => state.app?.userOrganizations);

  const isAdmin = useMemo(() => hasRight(user, RoleClaimsEnum.Admin), [user]);

  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const options = useMemo(
    () =>
      (userOrganizations ?? []).map((org) => ({
        ...org,
        id: Number(org.id),
        name: org.displayName
      })),
    [userOrganizations]
  );

  const filteredOptions = useMemo(() => {
    return options.filter(
      (org) =>
        org.name.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
        org.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(
    options.find((org) => org.id === appUser?.organizationId) || null
  );

  useEffect(() => {
    const currentOrg = options.find((org) => org.id === appUser?.organizationId);
    if (currentOrg) {
      setSelectedOrg(currentOrg);
    }
  }, [options, appUser?.organizationId]);

  const handleChange = async (e: React.ChangeEvent<{ value: unknown }>) => {
    const orgId = Number(e.target.value);
    if (!orgId) {
      return;
    }

    const org = options.find((o) => o.id === orgId) ?? null;
    setSelectedOrg(org);

    setIsLoading(true);
    const payload: UpdateOrgToUserRequestDto = { userId: appUser?.id ?? '', organizationId: orgId };

    try {
      await appUserService.UpdateOrgToUser(payload);

      // Refresh token so reload picks up the new organization claims.
      const refreshed = await refreshOrganizationToken();
      if (!refreshed.success) {
        setIsLoading(false);
        enqueueSnackbar(
          translatorService.Tranlate(refreshed.message ?? 'CHANGE_ORGANIZATION_REFRESH_TOKEN_ERROR', refreshed.message ?? ''),
          { variant: 'error' }
        );

        return;
      }

      window.location.reload();
    } catch (err) {
      captureAuthError('Organization Switch', err);
      setIsLoading(false);
      enqueueSnackbar(translatorService.Tranlate('GENERAL_ERROR', 'A aparut o eroare'), {
        variant: 'error'
      });
    }
  };

  const refreshOrganizationToken = async (): Promise<{ success: boolean; message?: string }> => {
    if (!refreshToken) {
      return { success: false };
    }

    const data = await refreshTokenShared(refreshToken);

    if (data && data.success) {
      const newToken = data.tokenResponse.access_token;
      const newRefreshToken = data.tokenResponse.refresh_token;

      // Await so the new token is written to localStorage before the reload (ClientApp2's persistor.flush equivalent).
      await (dispatch as any)(AppActionCreators.SetToken(newToken));
      await (dispatch as any)(AppActionCreators.SetRefreshToken(newRefreshToken));

      return { success: true };
    }

    return { success: false, message: data?.message };
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Box>
      <Select
        value={selectedOrg?.id ?? ''}
        onChange={handleChange}
        IconComponent={ExpandMore}
        displayEmpty
        disableUnderline
        className={classes.root}
        renderValue={(value) => (
          <Box display="flex" alignItems="center" justifyContent="center">
            {isLoading && <CircularProgress size={14} style={{ color: '#fff', marginRight: 8 }} />}
            <span className={classes.label}>
              {value
                ? selectedOrg?.name
                : translatorService.Tranlate(
                    'SELECT_ORGANIZATION_BADGE_PLACEHOLDER',
                    'Selectează organizația'
                  )}
            </span>
          </Box>
        )}
        MenuProps={{
          PaperProps: { style: { maxHeight: '50vh', width: 320 } },
          autoFocus: false,
          getContentAnchorEl: null,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' }
        }}
      >
        <Box
          className={classes.searchBox}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            autoFocus
            value={searchTerm}
            placeholder={translatorService.Tranlate(
              'SEARCH_ORGANIZATION_TEXT',
              'Caută organizație'
            )}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {filteredOptions.length === 0 && (
          <MenuItem disabled value="">
            {searchTerm
              ? translatorService.Tranlate('NO_ORGANIZATIONS_AVAILABLE', 'Nicio organizație găsită')
              : translatorService.Tranlate('NO_ORGANIZATIONS_TEXT', 'Nicio organizație')}
          </MenuItem>
        )}

        {filteredOptions.map((opt) => (
          <MenuItem key={opt.id} value={opt.id} title={opt.name} className={classes.menuItem}>
            {opt.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default OrganizationsBadge;
